/* global */

import { post } from '@rails/request.js'
import Vue from 'vue'
import VueResource from 'vue-resource'
import { PiniaVuePlugin, createPinia, mapStores } from 'pinia'
import PortalVue from 'portal-vue'

import App from '@/apps/fast/App.vue'
import { useMainStore } from '@/apps/fast/store'
import StepTitle from '@/apps/fast/components/StepTitle.vue'
import StepNav from '@/apps/fast/components/StepNav.vue'
import '@/utils/vue_filters'

Vue.use(PortalVue)
Vue.use(PiniaVuePlugin)
Vue.use(VueResource)

Vue.component('StepTitle', StepTitle)
Vue.component('StepNav', StepNav)

Vue.http.interceptors.push((request, next) => {
  request.headers.set('Content-Type', 'application/json')
  if (document.getElementsByName('csrf-token')[0])
    request.headers.set('X-CSRF-Token', document.getElementsByName('csrf-token')[0].content)
  next()
})

function cleanText(text) {
  if (typeof text !== 'string') {
    return ''
  }
  return text.replace(/^\s+/, '').replace(/\s+/g, ' ')
}

export const fastApp = (async function initializeFastApp() {
  const pinia = createPinia()
  pinia.use(({ store }) => {
    store.$http = Vue.http
  })

  const appEl = document.createElement('div')
  appEl.id = 'fast-app'
  document.body.appendChild(appEl)

  const template = document.getElementById('fast-data')
  const { body } = await Vue.http.get('/fast-data', {
    params: {
      seo_tagging_page_id: template?.dataset.seoTaggingPage,
      project_id: template?.dataset.project
    }
  })
  const store = useMainStore(pinia)
  store.$patch(body)

  window.FastApp = new Vue({
    el: '#fast-app',
    data() {
      return {
        sessionId: null
      }
    },
    computed: {
      ...mapStores(useMainStore)
    },
    methods: {
      cleanText,
      isAndroid() {
        return /Android/i.test(navigator.userAgent)
      },
      launch(source, options = {}) {
        // This id is used as key in App.vue template. This permit to reset the state of components every time the app is launched.
        this.sessionId = new Date().getTime()
        this.mainStore.launch(source, options)
        setTimeout(() => {
          document.querySelector('body').style.position = 'fixed'
        }, 600) // Wait for openning animation to finish
        window.history.pushState({ fastOpened: true }, '')
        window.addEventListener('popstate', this._close)
      },
      close() {
        window.history.back()
      },
      _close() {
        document.querySelector('body').style.position = 'static'
        if (!this.mainStore.lightMode && this.mainStore.project?.url) {
          window.location.href = this.mainStore.project.url
        } else {
          this.mainStore.reset()
        }

        window.removeEventListener('popstate', this._close)
      },
      sendMatomoPageView(name) {
        let eventName = String(name)
        if (this.mainStore.wellDescribed !== null) {
          eventName += this.mainStore.wellDescribed === false ? '/3q' : '/1q'
        }
        if (this.userTypology) {
          eventName = eventName + '/' + this.userTypology
        }

        const customUrl = `${window.location.origin}/megafast/${eventName}`
        window._paq.push(['setCustomUrl', customUrl])
        window._paq.push(['trackPageView'])
      },
      async checkUser(email = null) {
        const { body } = await this.$http.post('/projects/check-user', { email })
        return body
      },
      async createAndStoreProject() {
        this.mainStore.submitting = true
        this.mainStore.errors = {}
        this.sendMatomoPageView('submitted')

        try {
          const response = await post('/projects', {
            body: this.mainStore.requestBody,
            responseKind: 'json'
          })
          const body = await response.json

          if (response.ok) this.successfulProjectCreationCallback(body)
          else {
            this.failedProjectCreationCallback(body)
            const error = new Error('Cannot save project (validation failed)')
            throw error
          }
          return body
        } catch (error) {
          this.sendMatomoPageView('errored')
          throw error
        } finally {
          this.mainStore.submitting = false
        }
      },
      successfulProjectCreationCallback(body) {
        this.sendProjectCreationAnalytics()
        this.mainStore.submitting = false
        this.mainStore.setProject(body)
      },
      sendProjectCreationAnalytics() {
        this.sendMatomoPageView('finished')
        const matomoUrl = new URL(window.location.href)
        matomoUrl.searchParams.set('publication', 'projet')
        window._paq.push(['setCustomUrl', matomoUrl.href])
        window._paq.push(['trackPageView'])
      },
      failedProjectCreationCallback(body) {
        if (typeof body.errors === 'object') {
          this.mainStore.errors = body.errors
        }
        if ('password_url' in body) {
          this.passwordUrl = body.password_url
          this.sendMatomoPageView('incorrect-password')
        }
      }
    },
    render(h) {
      return h(App)
    },
    pinia
  })
})()
